// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { environment } from '@shared/environment';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountWorkflowData } from '@shared/advisory/account-workflow/providers';

export const useIdentityVerification = () => {
  const { t } = useTranslation();

  const {
    markStepAsCompleted,
    profileData,
    setVerificationCompleted,
    verificationCompleted,
  } = useAccountWorkflowData();

  const identityVerificationUrl = useMemo(() => {
    const externalId = profileData?.id;

    if (!externalId) {
      return undefined;
    }

    return environment.SERVICES.LLEIDA_EKYC_BASE_URL?.concat(
      'external-id/',
      encodeURIComponent(externalId),
    );
  }, [profileData?.id]);

  const qrCodeSrc = useMemo(() => {
    if (!identityVerificationUrl) {
      return undefined;
    }

    return environment.SERVICES.QR_CODE.BASE_URL?.concat(
      '?size=250x250&data=',
      encodeURIComponent(identityVerificationUrl),
    );
  }, [identityVerificationUrl]);

  const qrAlt = useMemo(() => {
    return t('account-opening.screens.identity-verification.qr-alt', {
      ns: 'components',
    });
  }, [t]);

  const onNext = useCallback(async () => {
    markStepAsCompleted('identity-verification');

    return true;
  }, [markStepAsCompleted]);

  return {
    qrCodeSrc,
    qrAlt,
    identityVerificationUrl,
    onNext,
    verificationCompleted,
    setVerificationCompleted,
  };
};
